import React, {useCallback, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {AuthCredentialType, IconType, ProfileEditVerifMethod} from 'types'
import {Button, Icon, Modal, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'

interface ItemContainerProps {
  isSelected: boolean
}
const StyledModal = styled(Modal)`
  box-sizing: border-box;
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
`
const StyledTitle = styled(Paragraph)`
  text-align: center;
  margin-bottom: ${convertUnit(20)};
`
const StyledSeparator = styled.div`
  height: ${convertUnit(1)};
  width: 100%;
  margin: ${convertUnit(12)} 0;
  background-color: ${({theme}) => theme.gray_1};
`
const StyledItemContainer = styled.div<ItemContainerProps>`
  padding: ${convertUnit(16)} ${convertUnit(20)};
  gap: ${convertUnit(20)};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: ${convertUnit(12)};
  cursor: pointer;
  ${({isSelected, theme}) => ({
    border: isSelected ? `${convertUnit(2)} solid ${theme.primary_5}` : 'unset',
    borderRadius: convertUnit(8),
  })}
`
const StyledItemContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  align-items: center;
`
const StyledItemContainerText = styled.div`
  display: flex;
  flex-direction: column;
  word-break: break-all;
`
const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: fill-available;
  margin-top: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  width: 100%;
`

interface GiftShopEditProfileModalVerifMethodProps {
  visible: boolean
  toggleModal(): void
  method: AuthCredentialType
  oldCredential: string
  onSelect(method: ProfileEditVerifMethod): void
  loading?: boolean
  isAddCredential?: boolean
  showKyc?: boolean
}
export default function GiftShopEditProfileModalVerifMethod({
  visible,
  toggleModal,
  method,
  oldCredential,
  onSelect,
  loading,
  isAddCredential = false,
  showKyc = false,
}: GiftShopEditProfileModalVerifMethodProps) {
  const {translate} = useTranslation()
  const {verifMethodEmail, verifMethodPhone} =
    useSelector('editProfileState') || {}
  // INTENTIONAL FLIP BELOW DUE TO CROSS VERIFICATION
  const [selected, setSelected] = useState<ProfileEditVerifMethod>(
    method === 'email'
      ? isAddCredential
        ? verifMethodPhone || 'email'
        : verifMethodEmail || 'email'
      : isAddCredential
      ? verifMethodEmail || 'whatsapp'
      : verifMethodPhone || 'whatsapp',
  )
  const handleRenderIcon = useCallback(
    (editCredentialMethod: ProfileEditVerifMethod): IconType => {
      switch (editCredentialMethod) {
        case 'email':
          return 'email-ol'
        case 'phone':
          return 'message-ol'
        case 'whatsapp':
          return 'whatsapp-ol'
        default:
          return 'face-scan'
      }
    },
    [],
  )
  const handleRenderMethodText = useCallback(
    (editCredentialMethod: ProfileEditVerifMethod) => {
      switch (editCredentialMethod) {
        case 'email':
          return translate('giftShop:emailTo')
        case 'phone':
          return translate('giftShop:textTo')
        case 'whatsapp':
          return translate('giftShop:whatsappTo')
        default:
          return 'KYC Selfie'
      }
    },
    [translate],
  )
  const handleRenderItem = useCallback(
    (editCredentialMethod: ProfileEditVerifMethod) => (
      <StyledItemContainer
        isSelected={editCredentialMethod === selected}
        onClick={() => setSelected(editCredentialMethod)}>
        <StyledItemContentContainer>
          <Icon
            type={handleRenderIcon(editCredentialMethod)}
            size={40}
            color="primary_5"
          />
          <StyledItemContainerText>
            <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
              {handleRenderMethodText(editCredentialMethod)}
            </Paragraph>
            {editCredentialMethod === 'kyc' || !oldCredential ? null : (
              <Paragraph fontWeight="medium" color="gray_3">
                {oldCredential}
              </Paragraph>
            )}
          </StyledItemContainerText>
        </StyledItemContentContainer>
        {editCredentialMethod === selected ? (
          <Icon type="check" color="primary_5" />
        ) : null}
      </StyledItemContainer>
    ),
    [handleRenderIcon, handleRenderMethodText, oldCredential, selected],
  )
  return (
    <StyledModal visible={visible} onBackdropPress={toggleModal}>
      <StyledTitle fontSize="l" fontWeight="bold">
        {translate('giftShop:selectVerificationMethod')}
      </StyledTitle>
      <Paragraph fontWeight="medium" color="gray_5">
        {translate(
          isAddCredential
            ? 'giftShop:addCredentialVerifMethod'
            : 'giftShop:editCredentialVerifViaOldCredential',
          {
            context: method,
          },
        )}
      </Paragraph>
      {method === 'email' ? (
        handleRenderItem('email')
      ) : (
        <>
          {handleRenderItem('whatsapp')}
          {handleRenderItem('phone')}
        </>
      )}
      {showKyc && (
        <>
          <StyledSeparator />
          <Paragraph fontWeight="medium" color="gray_5">
            {translate(
              isAddCredential
                ? 'giftShop:addCredentialCantAccessCredential'
                : 'giftShop:editCredentialCantAccessOldCredential',
              {
                context: method,
              },
            )}
          </Paragraph>
          {handleRenderItem('kyc')}
        </>
      )}
      <StyledButtonContainer>
        <StyledButton
          label={translate('global:cancel')}
          color="primary_5"
          backgroundColor="white_3"
          className="pressable"
          onClick={toggleModal}
        />
        <StyledButton
          label={translate('global:select')}
          className="pressable"
          isLoading={loading}
          onClick={() => {
            onSelect(selected)
          }}
        />
      </StyledButtonContainer>
    </StyledModal>
  )
}
